import React, { FC, ReactElement, ReactNode } from 'react';
import { useCoverSlideshowLoader } from 'ts/client/gallery/hooks';
import { CoverSlideshowProvider } from 'ts/common/contexts/CoverSlideshowContext';
import type { ICoverPhoto } from 'ts/common/types';

interface Props {
    /** The slideshow cover to load. */
    coverPhoto: ICoverPhoto;
    /** Shown until the slideshow can start playing (or until loading it gives up). */
    fallback: ReactElement;
    children: ReactNode;
}

/**
 * Holds the intro page's loading state until a slideshow cover is ready to play, then renders the
 * page with that loaded slideshow available to `CoverPhoto` — so the intro opens on the slideshow
 * itself rather than on its first-slide poster.
 *
 * This is a separate component, mounted by `Intro` only when the cover really is a slideshow,
 * rather than a hook called from `Intro` directly: loading a slideshow reads the client gallery's
 * event data, and a plain photo cover (or the Studio branding preview) has no business requiring
 * that just to render a still image.
 */
const CoverSlideshowGate: FC<Props> = ({ coverPhoto, fallback, children }) => {
    const { coverSlideshow, isSettled } = useCoverSlideshowLoader(coverPhoto);

    if (!isSettled) {
        return fallback;
    }

    return <CoverSlideshowProvider value={coverSlideshow}>{children}</CoverSlideshowProvider>;
};

export default CoverSlideshowGate;
